import { useEffect, useRef, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { notify } from '@/lib/notify';
import { type LessonMetadata } from '@/lib/api-client';
import { useSaveDraftPrompt } from '@/hooks/lesson/useSources';
import { cn } from '@/lib/utils';
import { RetryButton } from '../../common/RetryButton';

/**
 * Prompt-only panel - replaces the legacy `ContextEditor`.
 *
 * The user's prompt is the single editable surface here. Saving stores
 * a draft on `staged_prompt`; the version is created later via the
 * bottom action bar's "Create new version" button. Title/tags/overview
 * are managed elsewhere - this panel exists to let the user iterate on
 * the lesson's *intent*.
 */

type StepState =
  | 'not_started'
  | 'queued'
  | 'processing'
  | 'completed'
  | 'failed';

export interface PromptOverviewPanelProps {
  lessonId: string;
  lesson: LessonMetadata;
  overviewStatus: StepState;
  /** Disables save during an in-flight regeneration to avoid stacking. */
  byteSelectionStatus: StepState;
  /**
   * Read-only mode - set when the user is viewing a non-latest version.
   * Disables the textarea, suppresses autosave, and swaps the helper copy
   * to direct the user back to the latest version for edits.
   */
  isLocked?: boolean;
  /** Why it is locked — see `StagedActionBar.lockReason`. */
  lockReason?: 'past-version' | 'building';
}

export function PromptOverviewPanel({
  lessonId,
  lesson,
  overviewStatus,
  byteSelectionStatus,
  isLocked = false,
  lockReason = 'past-version',
}: PromptOverviewPanelProps) {
  // The "live" prompt the user should see is the draft if one exists,
  // otherwise the persisted user_prompt. Comparisons against "dirty" are
  // against this baseline - saving "the same value" clears the draft.
  const baselinePrompt = (
    lesson.staged_prompt ??
    lesson.user_prompt ??
    ''
  ).trim();
  const hasDraft = lesson.staged_prompt != null;

  // Local draft. Sync from server when clean; preserve user edits when dirty.
  const [promptDraft, setPromptDraft] = useState(
    lesson.staged_prompt ?? lesson.user_prompt ?? ''
  );
  const promptDirty = promptDraft.trim() !== baselinePrompt;

  useEffect(() => {
    if (!promptDirty) {
      setPromptDraft(lesson.staged_prompt ?? lesson.user_prompt ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lesson.user_prompt, lesson.staged_prompt]);

  const promptMutation = useSaveDraftPrompt(lessonId);

  const isSaving = promptMutation.isPending;
  const inputsDisabled = byteSelectionStatus === 'processing';
  // Initial generation has no prior version to "re-generate" - the lesson
  // is just being built for the first time. After a user-triggered new
  // version (current_version flips past 1.0), the label is correct.
  const isInitialGeneration = lesson.current_version === '1.0';

  // Auto-grow the textarea up to a sensible cap so it feels document-like.
  const promptRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = promptRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 360)}px`;
  }, [promptDraft]);

  // Debounced autosave. Fires 800ms after the user stops typing, only when
  // the trimmed draft differs from the baseline and is non-empty. Empty
  // values are skipped so an accidental select-all + delete doesn't wipe
  // the prompt. The mutation persists `staged_prompt` server-side - no
  // version is created until the user hits "Create vN.0".
  const [savedFlash, setSavedFlash] = useState(false);
  const lastSavedValueRef = useRef<string>(baselinePrompt);
  useEffect(() => {
    if (inputsDisabled) return;
    // Don't fire autosave when the textarea is locked (user is viewing an
    // older version). Editing past versions isn't allowed; keystrokes here
    // wouldn't even reach the textarea, but guard the mutation anyway.
    if (isLocked) return;
    const trimmed = promptDraft.trim();
    if (!trimmed) return;
    if (trimmed === lastSavedValueRef.current) return;
    const handle = setTimeout(() => {
      promptMutation.mutate(trimmed, {
        onSuccess: () => {
          lastSavedValueRef.current = trimmed;
          setSavedFlash(true);
          setTimeout(() => setSavedFlash(false), 1800);
        },
        onError: err => {
          const msg =
            err instanceof Error ? err.message : 'Failed to save draft';
          notify.error(msg);
        },
      });
    }, 800);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promptDraft, inputsDisabled, isLocked]);

  // Keep the ref in sync with server state when no edit is in flight, so
  // a remote-driven update (e.g. discard, regenerate) doesn't trigger a
  // redundant autosave on next keystroke.
  useEffect(() => {
    if (!promptDirty) {
      lastSavedValueRef.current = baselinePrompt;
    }
  }, [baselinePrompt, promptDirty]);

  // Render --------------------------------------------------------------------

  // The prompt is the user's own words, so it renders as soon as the lesson
  // metadata exists - it does not wait on the overview. A failed overview
  // (title + summary) does not stop the lesson building, so it is a notice
  // above the prompt with its own Retry, not a replacement for it.
  return (
    <div className='flex h-full min-h-0 flex-col'>
      {overviewStatus === 'failed' && (
        <div className='mx-3 mt-3 space-y-2 rounded-lg border border-border bg-muted/40 p-3'>
          <p className='text-sm font-medium text-destructive-emphasis'>
            Failed to generate lesson overview
          </p>
          <p className='text-xs text-muted-foreground'>
            {lesson.error?.message ||
              'The overview task hit an error. Retry to try again.'}
          </p>
          <RetryButton lessonId={lessonId} step='overview' />
        </div>
      )}
      {/* Editable region. Status appears inline with the helper text and
          reflects autosave state - no save/discard buttons. */}
      <div className='flex min-h-0 flex-1 flex-col p-3'>
        <p className='mb-3 text-xs text-muted-foreground'>
          {!isLocked
            ? 'Click to edit and reshape this lesson.'
            : lockReason === 'building'
              ? 'You can edit this once the lesson finishes building.'
              : 'Viewing a past version. Switch to the latest to edit.'}
          {/* The save state is announced, not just tinted. A live region has to
              be MOUNTED before its content changes or nothing is announced, so
              this span is always rendered and only its contents swap. There are
              no save/discard buttons here — autosave is the whole interaction —
              which makes this chip the only feedback there is. */}
          <span role='status'>
            {inputsDisabled && !isInitialGeneration ? (
              // Initial build: progress lives in the center build view, so we
              // don't show a competing "Generating" spinner here. Only a
              // user-triggered regeneration (v2+) shows this inline indicator.
              <span className='ml-2 inline-flex items-center gap-1 align-middle text-xs text-muted-foreground'>
                <Loader2 className='h-3 w-3 animate-spin' />
                Regenerating
              </span>
            ) : isSaving ? (
              <span className='ml-2 inline-flex items-center gap-1 align-middle text-xs text-muted-foreground'>
                <Loader2 className='h-3 w-3 animate-spin' />
                Saving
              </span>
            ) : savedFlash ? (
              // `text-success-emphasis`, not `emerald-600`: 11px text needs
              // 4.5:1 and emerald-600 is 3.50:1 on this panel. The token is
              // 5.79:1 and self-swaps in dark, so the dark override is gone.
              <span className='ml-2 inline-flex items-center gap-1 align-middle text-xs text-success-emphasis'>
                <Check className='h-3 w-3' strokeWidth={3} />
                Saved
              </span>
            ) : hasDraft ? (
              // Same fix, and amber-600 was the worse of the two at 2.96:1.
              <span className='ml-2 inline-flex items-center gap-1 align-middle text-xs text-warning-emphasis'>
                <span className='h-1.5 w-1.5 rounded-full bg-amber-500' />
                Draft
              </span>
            ) : null}
          </span>
        </p>

        <textarea
          ref={promptRef}
          id='prompt-overview-prompt'
          // The placeholder was the only accessible name this field had. axe
          // accepts that, but a name that exists only until the user types is
          // not one — and there is no visible label to point `htmlFor` at.
          aria-label='Lesson prompt'
          value={promptDraft}
          onChange={e => setPromptDraft(e.target.value)}
          maxLength={2000}
          disabled={inputsDisabled}
          readOnly={isLocked}
          className={cn(
            'w-full flex-1 resize-none border-0 bg-transparent p-0 text-sm leading-relaxed',
            '!outline-none focus:!outline-none focus:!ring-0 focus:!ring-offset-0',
            'focus-visible:!outline-none focus-visible:!ring-0 focus-visible:!ring-offset-0',
            'disabled:cursor-not-allowed disabled:opacity-60',
            isLocked && 'cursor-not-allowed opacity-70',
            'placeholder:text-muted-foreground/60'
          )}
          placeholder='Describe what you want this lesson to cover…'
        />
      </div>
    </div>
  );
}
